import { useQuery } from '@tanstack/react-query';
import { Link, useSearch } from '@tanstack/react-router';
import {
  Alert,
  Badge,
  Card,
  Filters,
  Icon,
  NotProvided,
  Page,
  PageHeader,
  Pagination,
  rowLinkClasses,
  Table,
  type FilterFieldSlot,
} from '@evogard/ui';
import { loadFailureMessage } from '@evogard/domain';
import { api } from '../../lib/api.js';
import { useSessionContext } from '../../lib/session.js';
import { useListSearch, useSearchTerm, useClampedPage } from '../../lib/list-search.js';
import { MirrorStamp, StatusBadge } from './common.js';

/**
 * O cadastro de associados da praça (14/10/2026) — a lista que leva à ficha.
 *
 * Até aqui o associado só aparecia como o dono dentro da ficha de um carro. A
 * busca é um campo só, pelo motivo de `VehiclesScreen`: quem atende tem um
 * dado na mão (nome, documento ou código). O filtro de situação vai pelo NOME:
 * o código de situação vem vazio na maior parte do cadastro (o dono aninhado
 * no veículo só traz a descrição), e as opções saem do próprio cadastro.
 */
export function MembersScreen() {
  const { data: context } = useSessionContext();
  const tenantId = context?.currentTenant.id;

  /* página, tamanho, busca e filtros na URL — o "voltar" devolve a lista como estava */
  const search = useSearch({ from: '/authenticated/erp/associados' });
  const list = useListSearch(search);
  const { text, setText } = useSearchTerm(search.q, (q) => list.setFilters({ q }));
  const term = search.q ?? '';
  const applied = {
    situacao: search.situacao ?? '',
    tipoPessoa: search.tipoPessoa ?? '',
  };
  const { page, perPage } = list;

  const query = useQuery({
    queryKey: ['erp', 'associados', tenantId, term, applied, page, perPage],
    queryFn: async () => {
      const r = await api.erp.searchMembers({
        body: {
          term: term.length >= 2 ? term : undefined,
          situacao: applied.situacao || undefined,
          tipoPessoa: applied.tipoPessoa || undefined,
          page,
          perPage,
        },
      });
      if (r.status !== 200)
        throw new Error(loadFailureMessage(r.status, 'os associados'));
      return r.body;
    },
    placeholderData: (previous, previousQuery) =>
      previousQuery?.queryKey[2] === tenantId ? previous : undefined,
  });

  const filters = useQuery({
    queryKey: ['erp', 'associados', 'filtros', tenantId],
    queryFn: async () => {
      const r = await api.erp.memberFilters();
      return r.status === 200 ? r.body : null;
    },
  });

  const fields: FilterFieldSlot[] = [
    {
      type: 'selecao',
      name: 'situacao',
      label: 'Situação',
      placeholder: 'Todas',
      options: (filters.data?.statuses ?? []).map((s) => ({
        value: s.label,
        label: `${s.label} (${s.total.toLocaleString('pt-BR')})`,
      })),
    },
    {
      type: 'selecao',
      name: 'tipoPessoa',
      label: 'Tipo de pessoa',
      placeholder: 'Todos',
      options: (filters.data?.personTypes ?? []).map((t) => ({
        value: t.label,
        label: `${t.label} (${t.total.toLocaleString('pt-BR')})`,
      })),
    },
  ];

  const result = query.data;
  /* a página além do fim (link antigo, lista que encolheu) vai para a última */
  useClampedPage(page, perPage, result && 'total' in result ? result.total : undefined);
  const filtered = Boolean(term || applied.situacao || applied.tipoPessoa);

  return (
    <Page measure="cheia">
      <PageHeader title="Associados">
        O cadastro de {context?.currentTenant.nome}, como o ERP o entrega.
      </PageHeader>

      {query.isError && <Alert tone="erro">{query.error.message}</Alert>}

      {(!query.isError || result) && (
        <Card
          title={result === undefined ? 'Carregando…' : `${result.total} associado(s)`}
          descricao={<MirrorStamp at={result?.syncedAt ?? null} />}
          bleed
        >
          <Table
            paginate={false}
            search={{
              label: 'Pesquisar associados',
              placeholder: 'Nome, CPF/CNPJ ou código do associado',
              value: text,
              onChange: setText,
            }}
            toolbar={
              <Filters
                values={applied}
                fields={fields}
                onApply={(values) =>
                  list.setFilters({
                    situacao: String(values.situacao ?? '') || undefined,
                    tipoPessoa: String(values.tipoPessoa ?? '') || undefined,
                  })
                }
              />
            }
            label="Associados do cadastro"
            items={result?.estado === 'lida' ? result.items : []}
            loading={query.isLoading}
            rowKey={(m) => m.code}
            empty={
              filtered
                ? 'Nenhum associado com esses filtros.'
                : 'O cadastro desta base está vazio.'
            }
            rowLink={(m) => (
              <Link
                to="/erp/associados/$code"
                params={{ code: m.code }}
                aria-label={`Abrir ${m.nome ?? `o associado ${m.code}`}`}
                className={rowLinkClasses}
              >
                <Icon nome="seta-direita" size={16} />
              </Link>
            )}
            columns={[
              {
                title: 'Nome',
                alignment: 'inicio' as const,
                minWidth: '16rem',
                cell: (m) => m.nome ?? <NotProvided>sem nome</NotProvided>,
              },
              {
                title: 'Documento',
                minWidth: '10rem',
                cell: (m) =>
                  m.documento ? (
                    <span className="tabular-nums">{m.documento}</span>
                  ) : (
                    <NotProvided />
                  ),
              },
              {
                title: 'Tipo',
                minWidth: '7rem',
                cell: (m) =>
                  m.tipoPessoa ? (
                    <Badge tone="neutro">{m.tipoPessoa}</Badge>
                  ) : (
                    <NotProvided>não informado</NotProvided>
                  ),
              },
              {
                title: 'Situação',
                minWidth: '10rem',
                cell: (m) =>
                  m.situacao ? <StatusBadge situacao={m.situacao} /> : <NotProvided />,
              },
              {
                title: 'Cidade',
                minWidth: '10rem',
                cell: (m) =>
                  m.cidade ? (
                    `${m.cidade}${m.estado ? `/${m.estado}` : ''}`
                  ) : (
                    <NotProvided />
                  ),
              },
              {
                title: 'Veículos',
                minWidth: '6rem',
                cell: (m) => <span className="tabular-nums">{m.vehicles}</span>,
              },
              {
                title: 'Código no ERP',
                minWidth: '7rem',
                cell: (m) => m.erpCode ?? <NotProvided />,
              },
            ]}
          />

          {result?.estado === 'lida' && (
            <Pagination
              page={result.page}
              perPage={result.perPage}
              total={result.total}
              onChange={list.setPage}
              onPerPageChange={list.setPerPage}
              label="associados"
              className="border-t border-borda-suave px-5 py-4"
            />
          )}
        </Card>
      )}
    </Page>
  );
}
